declare global {
  interface Window {
    mapboxgl: any;
  }
}

export type Subtopic = { label: string; value: string; note?: string };

/**
 * A legacy field value.
 *
 * The legacy renderer (`StatePanel`'s `DetailBody`) has exactly two branches for
 * these: a single display string, or a list of labelled subtopics. This names
 * that pair precisely rather than widening to something permissive — it is the
 * legacy presentation contract, not a way to silence the compiler.
 */
export type LegacyField = string | Subtopic[];

/**
 * The legacy per-jurisdiction record consumed by the current UI.
 *
 * TRANSITIONAL TYPE. This is the shape `public/data/llc-data.json` is generated
 * in and the shape `StatePanel` reads. The canonical model lives in
 * `src/canonical` and is projected into this shape by `toStateInfo`, so that
 * data can migrate jurisdiction by jurisdiction without the UI being rewritten.
 * New work belongs in the canonical model; this type exists to keep the existing
 * components compiling and rendering unchanged.
 *
 * Field types below are measured from the 51 live records rather than assumed:
 *
 *   - `formationFee` is a subtopic list in all 51 records.
 *   - Six fields carry either a string or a subtopic list depending on the
 *     jurisdiction, and are typed `LegacyField`.
 *   - `dba` is absent from 4 records, so it alone is optional.
 *   - Every other field is a string in all 51 records and is typed `string`.
 *   - `requirements` is the only string list.
 *
 * The previous declaration typed five of these as `string` while the data held
 * subtopic lists. Because the JSON is fetched at runtime and cast, the compiler
 * never saw the mismatch; these types are now truthful so that it does.
 */
export type StateInfo = {
  typesOfFormation: string;
  formationFee: Subtopic[];
  foreignFiling: LegacyField;
  expeditedFee: string;
  managementStructure: LegacyField;
  purposeNaics: LegacyField;
  pob: string;
  certificateOfGoodStanding: string;
  renewalFee: string;
  renewalLateFee: string;
  renewalSchedule: string;
  reinstatement: string;
  dissolutionFee: LegacyField;
  nameAfterDissolution: string;
  amendmentFee: LegacyField;
  amendmentExpediteFee: string;
  amendmentTypes: string;
  /** Absent in 4 of the 51 records. */
  dba?: LegacyField;
  dbaFee: string;
  dbaRenewalSchedule: string;
  certifiedCopies: string;
  apostille: string;
  miscellaneous: string;
  requirements: string[];
};

/** Keyed by the jurisdiction's legacy display name, e.g. "Alabama", "Washington DC". */
export type LLCData = Record<string, StateInfo>;

export type ViewKey = "globe" | "pricing" | "services" | "training";

export type ViewOption = {
  key: ViewKey;
  label: string;
};

export const VIEW_OPTIONS: ViewOption[] = [
  { key: "globe", label: "Map" },
  { key: "pricing", label: "Formation Plans" },
  { key: "services", label: "Services" },
  { key: "training", label: "Continuing Training" },
];

export {};
